import * as React from "react";
import { useMemo, type ComponentType } from "react";
import { twMerge } from "tailwind-merge";
import { ExclamationCircleIcon } from "@components/icons";

type TColor = "grey" | "blue" | "green" | "red" | "purple" | "orange";

interface INotificationProps {
  /**
   * Notification title. Applicable only for "big" variant.
   */
  title?: string | JSX.Element;
  /**
   * Notification description text.
   */
  description: string | JSX.Element | JSX.Element[];
  /**
   * Notification variant.
   */
  variant?: "small" | "big";
  /**
   * Notification color scheme.
   */
  color?: TColor;
  /**
   * Image path. Applicable only for "big" variant;
   */
  imagePath?: string;
  /**
   * Icon. Applicable only for "small" variant;
   */
  icon?: ComponentType<React.SVGProps<SVGSVGElement>> | null;
  /**
   * Element that will be placed in button position.
   */
  button?: JSX.Element;
  /**
   *  Class name.
   */
  className?: string;
}

export const Notification: React.FC<INotificationProps> = ({
  title,
  description,
  variant = "small",
  color = "grey",
  imagePath = "/images/notification/bill-icon.png",
  icon: Icon = ExclamationCircleIcon,
  button,
  className,
}) => {
  const getColors = (clr: TColor) => {
    switch (clr) {
      case "blue":
        return ["bg-blue-light", "text-blue-dark"];
      case "green":
        return ["bg-green-light", "text-green-dark"];
      case "red":
        return ["bg-red-light", "text-red-dark"];
      case "purple":
        return ["bg-purple-light", "text-purple-dark"];
      case "orange":
        return ["bg-[#FFEDC1]", "text-base-black"];
      default:
        return ["bg-base-light-grey", "text-base-black"];
    }
  };

  const [bgColorClassname, textColorClassname] = useMemo(
    () => getColors(color),
    [color],
  );

  return (
    <div
      className={twMerge(
        "flex w-full flex-col items-start gap-5 md:flex-row md:items-center md:justify-between",
        bgColorClassname,
        textColorClassname,
        variant === "big" ? "p-4" : "p-3",
        variant === "big" ? "rounded-2xl" : "rounded-lg",
        className,
      )}
    >
      <div
        className={twMerge(
          "flex items-center md:items-start",
          variant === "big" ? "gap-4" : "gap-2",
        )}
      >
        {variant === "small" && Icon && (
          <Icon className={twMerge("h-5 w-5 shrink-0", textColorClassname)} />
        )}
        {variant === "big" && imagePath && (
          <img
            alt="Notification"
            className={twMerge(
              "object-fit h-[68px] w-auto mix-blend-multiply",
              textColorClassname,
            )}
            src={imagePath}
          />
        )}
        <div className="flex flex-col gap-1">
          {title && <div className="text-lg font-semibold">{title}</div>}
          <div className="hidden text-sm md:block">{description}</div>
        </div>
      </div>
      <div className="text-sm md:hidden">{description}</div>
      {button && (
        <div className="mt-auto flex-shrink-0 self-center md:mt-0">
          {button}
        </div>
      )}
    </div>
  );
};
